import React, { useRef } from 'react';
import {
    ArrowDown,
    ArrowUp,
    Image as ImageIcon,
    Trash2,
    Upload,
    X
} from 'lucide-react';
import {
    CdnObjectDto,
    CommunityWhitelabelDashboardWidgetDto,
    CommunityWhitelabelDashboardWidgetLayout,
    ImageType
} from '@api/ChallengeClient';
import { DateTimePicker } from '@components/date-picker';
import { useImageUpload } from '@components/hooks/use-image-upload';
import { Spinner } from '@components/spinner';
import { FieldError, FieldLabel } from '@components/textfield';
import LocalizedTextField from '@components/textfield/localized_textfield';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { LayoutSelect } from './layout-select';
import { LinkField } from './link-field';
import { imageUrl } from './widget-content';

function toDate(value?: string): Date | null {
    return value ? new Date(value) : null;
}

const WidgetImageField = ({
    image,
    onChange
}: {
    image?: CdnObjectDto;
    onChange(image?: CdnObjectDto): void;
}) => {
    const fileInput = useRef<HTMLInputElement>(null);
    const imageUpload = useImageUpload(ImageType.COMMUNITY, onChange);
    const url = imageUrl(image);

    return (
        <div className="flex flex-col gap-1.5">
            <FieldLabel label="Image" />
            <div className="flex items-center gap-2">
                <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
                    {imageUpload.loading ? (
                        <Spinner size="sm" />
                    ) : url ? (
                        <img
                            src={url}
                            alt=""
                            className="size-full object-cover"
                        />
                    ) : (
                        <ImageIcon className="size-4 text-muted-foreground" />
                    )}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={imageUpload.loading}
                    onClick={() => fileInput.current?.click()}
                >
                    <Upload />
                    {url ? 'Change' : 'Upload'}
                </Button>
                {url && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onChange(undefined)}
                    >
                        <X />
                        Remove
                    </Button>
                )}
                <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (file) {
                            imageUpload.handleUpload(file);
                        }
                    }}
                />
            </div>
            <FieldError error={imageUpload.error} />
        </div>
    );
};

export const DashboardWidgetEditor = ({
    id,
    communityId,
    position,
    count,
    widget,
    onChange,
    onMove,
    onRemove
}: {
    id: string;
    communityId: number;
    position: number;
    count: number;
    widget: CommunityWhitelabelDashboardWidgetDto;
    onChange(patch: Partial<CommunityWhitelabelDashboardWidgetDto>): void;
    onMove(offset: number): void;
    onRemove(): void;
}) => (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
        <div className="flex flex-wrap items-center gap-2">
            <Switch
                id={`${id}-enabled`}
                checked={widget.enabled}
                onCheckedChange={(enabled) => onChange({ enabled })}
            />
            <Label htmlFor={`${id}-enabled`} className="mr-auto">
                Widget {position + 1}
            </Label>
            <LayoutSelect
                value={widget.layout}
                onChange={(layout) => onChange({ layout })}
            />
            <div className="flex">
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Move up"
                    disabled={position === 0}
                    onClick={() => onMove(-1)}
                >
                    <ArrowUp />
                </Button>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Move down"
                    disabled={position === count - 1}
                    onClick={() => onMove(1)}
                >
                    <ArrowDown />
                </Button>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove widget"
                    onClick={onRemove}
                >
                    <Trash2 />
                </Button>
            </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
            <LocalizedTextField
                label="Title"
                type="text"
                size="small"
                value={widget.title ?? {}}
                onChange={(title) => onChange({ title })}
            />
            <LinkField
                communityId={communityId}
                link={widget.link}
                onChange={(link) => onChange({ link })}
            />
            <LocalizedTextField
                className="md:col-span-2"
                label="Text"
                type="text"
                multiline
                maxRows={2}
                value={widget.text ?? {}}
                onChange={(text) => onChange({ text })}
            />
            {widget.layout ===
                CommunityWhitelabelDashboardWidgetLayout.CALL_TO_ACTION && (
                <LocalizedTextField
                    className="md:col-span-2"
                    label="Button label"
                    type="text"
                    size="small"
                    value={widget.buttonLabel ?? {}}
                    onChange={(buttonLabel) => onChange({ buttonLabel })}
                />
            )}
            <div className="md:col-span-2">
                <WidgetImageField
                    image={widget.image}
                    onChange={(image) => onChange({ image })}
                />
            </div>
            <DateTimePicker
                label="Visible from"
                clearable
                value={toDate(widget.visibleFrom)}
                maxDate={toDate(widget.visibleTo) ?? undefined}
                onChange={(date) =>
                    onChange({ visibleFrom: date?.toISOString() })
                }
            />
            <DateTimePicker
                label="Visible until"
                clearable
                value={toDate(widget.visibleTo)}
                minDate={toDate(widget.visibleFrom) ?? undefined}
                onChange={(date) =>
                    onChange({ visibleTo: date?.toISOString() })
                }
            />
        </div>
    </div>
);
